<template>
    <div>
        <app-bar :title="club.name"/>

        <v-layout>
            <v-progress-linear
                v-if="overlay"
                absolute
                indeterminate
            ></v-progress-linear>

            <div class="pt-8 pb-12 px-8">
                <div class="pb-10">
                    <v-btn
                        v-if="permitted('admin_club_management')"
                        @click="edit"
                        class="sm mr-5"
                        color="primary"
                    >{{ t('general_action_edit') }}
                    </v-btn>
                    <v-btn
                        v-if="permitted('admin_club_management')"
                        class="sm mr-5"
                        color="primary"
                        outlined
                        @click.prevent="suspend(club)"
                    >
                        {{ club.enabled ? t('general_action_suspend') : t('general_action_unsuspend') }}
                    </v-btn>
                    <confirm-modal
                        v-if="permitted('admin_club_management')"
                        :title="t('general_title_confirm_action')"
                        @agree="del(club)"
                    >
                        <template #default="{ on, attrs }">
                            <v-btn
                                class="sm"
                                color="orange"
                                outlined
                                v-on="on"
                                v-bind="attrs"
                            >{{ t('general_action_delete') }}</v-btn>
                        </template>
                    </confirm-modal>
                </div>
                <div class="float-left mr-10" style="width: 394px; max-width: 394px">
                    <img
                        v-if="club.photoUrl"
                        :src="club.photoUrl"
                        width="150"
                    />
                    <div class="form-subtitle">{{ t('club_label_description') }}:</div>
                    <div class="form-subtitle-description" v-html="club.description"></div>
                    <template v-if="club.photos && club.photos.length">
                        <div class="form-subtitle mb-3">{{ t('club_label_header_photos') }}:</div>
                        <v-row>
                            <v-col
                                v-for="photo in club.photos"
                                :key="photo"
                                cols="6"
                            >
                                <v-sheet
                                    color="white"
                                    elevation="0"
                                    height="185"
                                    width="185"
                                    rounded="lg"
                                    class="pt-6"
                                >
                                    <v-img
                                        :lazy-src="photo"
                                        :src="photo"
                                        max-height="135"
                                        max-width="135"
                                        class="mx-auto"
                                    ></v-img>
                                </v-sheet>
                            </v-col>
                        </v-row>
                    </template>
                    <template v-if="club.landingPhotos && club.landingPhotos.length">
                        <div class="form-subtitle mb-3">{{ t('club_label_landing_photos') }}:</div>
                        <v-row>
                            <v-col
                                v-for="photo in club.landingPhotos"
                                :key="photo"
                                cols="6"
                            >
                                <v-sheet
                                    color="white"
                                    elevation="0"
                                    height="185"
                                    width="185"
                                    rounded="lg"
                                    class="pt-6"
                                >
                                    <v-img
                                        :lazy-src="photo"
                                        :src="photo"
                                        max-height="135"
                                        max-width="135"
                                        class="mx-auto"
                                    ></v-img>
                                </v-sheet>
                            </v-col>
                        </v-row>
                    </template>
                    <template v-if="usedColors().length">
                        <div class="form-subtitle mb-3">{{ t('club_label_branding') }}:</div>
                        <div
                            v-for="color in usedColors()"
                            :key="color.field"
                            class="d-flex align-center mb-2"
                        >
                            <v-sheet
                                :color="color.value"
                                height="20"
                                width="20"
                                class="bordered mr-3"
                            ></v-sheet>
                            <span class="mr-2">{{ t(`club_label_${color.field}`) }}</span>
                            <span class="text--secondary">{{ color.value }}</span>
                        </div>
                    </template>
                </div>
                <div class="float-left" style="max-width: calc(100% - 434px)">
                    <card-unit :items="items()">
                        <template #title>{{ t('club_label_id') }}: {{ club.id }}</template>
                    </card-unit>
                    <div class="d-flex align-center mt-4">
                        <span class="sub-label pr-2">{{ t('club_label_acquired_in_range') }}</span>
                        <date-picker-modal
                            :value="acquisitions.from"
                            @updated="(v) => { acquisitions.from = v; captureAcquisitions() }"
                        >
                            <template #default="{ on, attrs }">
                                <div v-on="on" v-bind="attrs">
                                    <v-text-field
                                        v-model="acquisitions.from"
                                        class="xs input-color-inherit"
                                        outlined
                                        dense
                                        hide-details
                                        readonly
                                        :label="t('general_label_from')"
                                    ></v-text-field>
                                </div>
                            </template>
                        </date-picker-modal>
                        <span class="sub-label px-2">-</span>
                        <date-picker-modal
                            :value="acquisitions.to"
                            @updated="(v) => { acquisitions.to = v; captureAcquisitions() }"
                        >
                            <template #default="{ on, attrs }">
                                <div v-on="on" v-bind="attrs">
                                    <v-text-field
                                        v-model="acquisitions.to"
                                        class="xs input-color-inherit"
                                        outlined
                                        dense
                                        hide-details
                                        readonly
                                        :label="t('general_label_to')"
                                    ></v-text-field>
                                </div>
                            </template>
                        </date-picker-modal>
                        <v-btn
                            v-if="acquisitions.from || acquisitions.to"
                            icon
                            small
                            class="ml-1"
                            @click="clearAcquisitionRange"
                        ><v-icon small>mdi-close</v-icon></v-btn>
                    </div>
                    <div class="caption grey--text mt-1">{{ t('club_hint_acquired_count') }}</div>
                </div>
                <div class="pt-8" style="clear: both">
                    <div class="form-subtitle mb-4">{{ t('club_label_sections') }}</div>
                    <v-card
                        v-for="section in club.sections"
                        :key="section.id"
                        elevation="0"
                        class="mb-4 pa-4 bordered"
                    >
                        <div class="customized-primary--text mb-2" style="font-weight:600">{{ section.name }}</div>
                        <v-simple-table dense>
                            <template #default>
                                <thead>
                                <tr>
                                    <th style="width:160px">{{ t('club_element_label_type') }}</th>
                                    <th>{{ t('club_element_label_name') }}</th>
                                    <th style="width:120px">{{ t('club_element_label_premium') }}</th>
                                    <th style="width:120px">{{ t('club_element_label_news') }}</th>
                                    <th style="width:200px">{{ t('club_element_label_published_at', { timezone }) }}</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr v-if="section.elements.length === 0">
                                    <td colspan="5" class="text-center">{{ t('general_text_no_data') }}</td>
                                </tr>
                                <tr
                                    v-for="element in section.elements"
                                    :key="element.id"
                                >
                                    <td>{{ t(`club_element_type_${element.type}`) }}</td>
                                    <td>{{ element.name }}</td>
                                    <td>{{ element.isPremium ? t('general_text_yes') : t('general_text_no') }}</td>
                                    <td>{{ element.isNews ? t('general_text_yes') : t('general_text_no') }}</td>
                                    <td>{{ publishedAtTitle(element) }}</td>
                                </tr>
                                </tbody>
                            </template>
                        </v-simple-table>
                    </v-card>
                </div>
            </div>
        </v-layout>
    </div>
</template>

<script lang="ts">
    import { computed, defineComponent, onMounted, reactive, toRefs } from '@vue/composition-api'
    import { useI18n } from 'vue-i18n-composable'
    import { useStore } from '@/store'
    import { useRouter } from '@/composition-api'
    import { useCountries, useEnums } from '@/core/defaults'
    import { browserTimezone, Club, CLUB_COLOR_FIELDS, ClubSectionElement } from '@/core/models/club'
    import moment from 'moment/moment'
    import ConfirmModal from '@/components/ConfirmModal.vue'
    import CardUnit from '@/components/CardUnit.vue'
    import DatePickerModal from '@/components/DatePickerModal.vue'
    import { useClubHttp } from '@/core/api/clubs'

    export default defineComponent({
        components: {
            ConfirmModal,
            CardUnit,
            DatePickerModal
        },

        setup(props) {
            const store = useStore()
            const router = useRouter()
            const i18n = useI18n()
            const id = router.currentRoute.params['id']

            const state = reactive({
                overlay: true,
            })

            const capture = () => {
                store.dispatch('club/get', { id: id, payload: {} }).then(() => {
                    state.overlay = false
                })
            }

            const clubModel = computed(() => <Club>store.getters['club/get'])

            // SSO-3614: swimmers acquired through the club's partner link — all time, and within
            // an optional from/to range. Read from its own admin endpoint, never from the club.
            const acquisitions = reactive({
                total: <number | null>null,
                inRange: <number | null>null,
                from: <string | undefined>undefined,
                to: <string | undefined>undefined,
            })

            const captureAcquisitions = () => {
                useClubHttp().acquisitions(<any>id, { from: acquisitions.from, to: acquisitions.to }).then((res: any) => {
                    acquisitions.total = res.data.total
                    acquisitions.inRange = res.data.in_range
                })
            }

            const clearAcquisitionRange = () => {
                acquisitions.from = undefined
                acquisitions.to = undefined
                captureAcquisitions()
            }
            const countries = useCountries()
            const enums = useEnums()

            const suspend = (item: Club) => {
                state.overlay = true
                store.dispatch('club/update', {
                    id: item.id,
                    payload: { enabled: ! item.enabled }
                }).then(() => {
                    store.dispatch('notify', { color: 'success', message: i18n.t('general_notice_data_updated') })
                    capture()
                })
            }

            const edit = () => router.push({ name: 'clubs.show', params: { id: <any>clubModel.value.id } })

            const del = (item: Club) => {
                state.overlay = true
                store.dispatch('club/delete', { id: item.id }).then(() => {
                    router.push({ name: 'clubs' })
                })
            }

            /** Only the colours an admin actually authored — the rest are derived by the app. */
            const usedColors = () => {
                return CLUB_COLOR_FIELDS
                    .map((field) => ({ field, value: (<any>clubModel.value.colors || {})[field] }))
                    .filter((color) => !! color.value)
            }

            /**
             * Empty means "visible now", which is a statement worth making in words — a blank
             * cell would read as missing data rather than as a deliberate setting. Rendered in
             * the same browser zone the edit page's picker works in (see the column header).
             */
            const publishedAtTitle = (element: ClubSectionElement): string => {
                return element.publishedAtLocal
                    ? moment(element.publishedAtLocal, moment.HTML5_FMT.DATETIME_LOCAL).format('DD/MM/YYYY HH:mm')
                    : <string>i18n.t('club_element_text_published_now')
            }

            const items = () => {
                return [
                    { text: i18n.t('club_label_country'), value: clubModel.value.country ? countries.countryTitle(clubModel.value.country) : '-' },
                    { text: i18n.t('club_label_city'), value: clubModel.value.city || '-' },
                    { text: i18n.t('club_label_club_type'), value: clubModel.value.clubType ? enums.clubTypeTitle(clubModel.value.clubType) : '-' },
                    { text: i18n.t('club_label_followers_count'), value: clubModel.value.followersCount },
                    { text: i18n.t('club_label_acquired_count'), value: acquisitions.total === null ? '-' : acquisitions.total },
                    ...(acquisitions.inRange === null ? [] : [
                        { text: i18n.t('club_label_acquired_in_range'), value: acquisitions.inRange },
                    ]),
                    { text: i18n.t('club_label_sort_order'), value: clubModel.value.sortOrder },
                    { text: i18n.t('general_label_creation_date'), value: clubModel.value.createdAt.format('DD/MM/YYYY') },
                    { text: i18n.t('general_action_published'), value: clubModel.value.enabled ? i18n.t('general_text_yes') : i18n.t('general_text_no') },
                ]
            }

            onMounted(() => {
                capture()
                captureAcquisitions()
            })

            return {
                capture,
                items,
                acquisitions,
                captureAcquisitions,
                clearAcquisitionRange,
                usedColors,
                publishedAtTitle,
                timezone: browserTimezone(),
                club: clubModel.value,
                edit,
                del,
                suspend,
                ...useI18n(),
                ...toRefs(state)
            }
        }
    })
</script>
